import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type { SettingGroupDefinition } from "../../settings-catalog/types";

export type FulfillmentFlatTeamSettingGroupId =
  | "fulfillment.packages"
  | "fulfillment.custom_imports"
  | "fulfillment.security.pin_sign_in"
  | "fulfillment.security.authorized_devices"
  | "fulfillment.international_shipping"
  | "fulfillment.tax_identifiers"
  | "fulfillment.print_profiles"
  | "fulfillment.printers"
  | "fulfillment.print_actions"
  | "fulfillment.customer_notifications"
  | "fulfillment.safeguards"
  | "fulfillment.package_photos"
  | "fulfillment.display"
  | "fulfillment.bundles"
  | "fulfillment.return_label_defaults"
  | "fulfillment.custom_field_mappings"
  | "fulfillment.merge_outcomes"
  | "fulfillment.merge_exceptions"
  | "fulfillment.split_outcomes"
  | "fulfillment.split_sku_mappings"
  | "fulfillment.split_exceptions"
  | "fulfillment.merge_suggestions"
  | "fulfillment.auto_merge"
  | "fulfillment.remember_shipment_choices"
  | "fulfillment.batch_suggestions"
  | "fulfillment.ship_from_locations"
  | "fulfillment.address"
  | "fulfillment.scan_to_verify"
  | "fulfillment.import_restrictions"
  | "fulfillment.printing"
  | "fulfillment.outbound_notifications"
  | "fulfillment.transfers"
  | "fulfillment.pick_sessions"
  | "fulfillment.replenishment"
  | "fulfillment.dashboard"
  | "fulfillment.work_orders"
  | "fulfillment.general"
  | "fulfillment.auto_split";

export const FulfillmentFlatTeamSettingGroups = [
  {
    id: "fulfillment.packages",
    title: "Packages",
    description:
      "Manage the packages available when creating and printing shipping labels, including dimensions, weight, and the default package.",
    searchLabels: [
      "Shipping packages",
      "Parcels",
      "Package dimensions",
      "Package weight",
      "Default package",
    ],
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: { kind: "provider", product: "fulfillment" },
    risk: {
      level: "medium",
      reason: "Package dimensions and weight affect shipping rates and labels.",
    },
    agentEditability: {
      mode: "never",
      reason: "Packages are managed in their dedicated merchant editor.",
    },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.custom_imports",
    title: "Custom imports",
    description:
      "Manage reusable CSV column mappings for importing orders from custom formats.",
    searchLabels: [
      "Custom import templates",
      "Custom import schemas",
      "CSV column mappings",
      "Schema name",
      "Row format",
      "Fields mapped",
    ],
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: { kind: "provider", product: "fulfillment" },
    risk: {
      level: "medium",
      reason: "Changes how CSV columns are interpreted when importing orders.",
    },
    agentEditability: {
      mode: "never",
      reason:
        "Import templates are managed in their dedicated merchant editor.",
    },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=presets-shortcuts",
    },
  },
  {
    id: "fulfillment.security.pin_sign_in",
    searchLabels: ["Allow email and PIN sign-in"],
    title: "PIN sign-in",
    description:
      "Allow warehouse staff to sign in with their email and PIN on an authorized device.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.security.methods.deviceAuthAndPin",
      product: "fulfillment",
    },
    risk: {
      level: "high",
      reason: "Changes the authentication methods warehouse staff can use.",
    },
    agentEditability: {
      mode: "never",
      reason: "A merchant must review changes to warehouse authentication.",
    },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=access-devices",
    },
  },
  {
    id: "fulfillment.security.authorized_devices",
    title: "Authorized devices",
    description:
      "Manage the browsers and devices allowed to use warehouse PIN sign-in.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: { kind: "provider", product: "fulfillment" },
    risk: {
      level: "high",
      reason:
        "Authorizing a device permits warehouse staff to sign in with a PIN on that device.",
    },
    agentEditability: {
      mode: "never",
      reason:
        "Only a merchant should authorize a device for warehouse sign-in.",
    },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=access-devices",
    },
  },
  {
    id: "fulfillment.international_shipping",
    title: "Default customs information",
    description: "Set customs defaults for international shipments.",
    searchLabels: ["International shipping", "International shipments"],
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.customs",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes customs declarations used for international shipments.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=international",
    },
  },
  {
    id: "fulfillment.tax_identifiers",
    title: "Tax identifiers",
    description:
      "Manage tax identifiers used on outbound customs declarations.",
    searchLabels: ["International shipping tax identifiers"],
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: { kind: "provider", product: "fulfillment" },
    risk: {
      level: "medium",
      reason: "Changes tax identification supplied on customs declarations.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=international",
    },
  },
  {
    id: "fulfillment.print_profiles",
    title: "Print profiles",
    searchLabels: [
      "Profile",
      "Profile name",
      "Orientation",
      "Number of copies",
      "Print packing slip on the same printer as the label",
      'Print packing slip on the 8.5" x 11" printer',
    ],
    description:
      "Create output profiles and assign the right printers and document behavior to each workstation.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes the documents and printers used during fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=printing",
    },
  },
  {
    id: "fulfillment.printers",
    title: "Printers & workstations",
    searchLabels: [
      "Redo Connect computer",
      "Name",
      "Print profile",
      "Default preset",
      "Printer",
      "Paper size",
      "Printer bin",
      "Rotate",
      "DPI",
      "Fit to page",
      "Duplex",
    ],
    description:
      "Connect local workstations to Redo and verify their printing setup.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes the documents and printers used during fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=printing",
    },
  },
  {
    id: "fulfillment.print_actions",
    title: "Fulfillment actions",
    description:
      "Choose the default fulfillment action and one-click printing preferences.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes the documents and printers used during fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=printing",
    },
  },
  {
    id: "fulfillment.customer_notifications",
    title: "Customer notifications",
    description:
      "Email customers about address corrections and pickup readiness.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.safeguards",
    title: "Fulfillment safeguards",
    description:
      "Prevent shipping beyond available inventory and require approval for selected actions.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.package_photos",
    title: "Package verification photos",
    description:
      "Capture photos of packed packages for dispute evidence and returns reference.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.display",
    title: "Display & units",
    description: "Display & units",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=display",
    },
  },
  {
    id: "fulfillment.bundles",
    title: "Bundles",
    description: "Bundles",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.return_label_defaults",
    title: "Return label defaults",
    description: "For return labels created during outbound fulfillment.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.custom_field_mappings",
    title: "Custom field mappings",
    description:
      "Choose existing order, product, and variant fields to use in fulfillment table columns and CSV exports, and set their display names in Redo.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=display",
    },
  },
  {
    id: "fulfillment.merge_outcomes",
    title: "Merged fulfillment outcomes",
    description:
      "Control the service, refunds, tags, and notes applied after fulfillments are merged.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=merging",
    },
  },
  {
    id: "fulfillment.merge_exceptions",
    title: "Auto-merge exceptions",
    description:
      "Do not automatically merge shipments when any of these conditions apply. Orders matching your auto-split conditions also won't be automatically re-merged.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=merging",
    },
  },
  {
    id: "fulfillment.split_outcomes",
    title: "Split shipment outcomes",
    description:
      "Choose how matching items are split and which tags to add afterward.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=splitting",
    },
  },
  {
    id: "fulfillment.split_sku_mappings",
    title: "Product SKU mappings",
    description:
      "Keep companion products with a trigger SKU when that SKU is split into its own shipment.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=splitting",
    },
  },
  {
    id: "fulfillment.split_exceptions",
    title: "Split exceptions",
    description:
      "Prevent splitting orders that match any of the selected criteria.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=splitting",
    },
  },
  {
    id: "fulfillment.merge_suggestions",
    title: "Merge suggestions",
    description:
      "Suggest merging fulfillments that meet every selected requirement.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.suggestMerge",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason: "Changes suggestions without automatically merging shipments.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=merging",
    },
  },
  {
    id: "fulfillment.auto_merge",
    title: "Auto-merge",
    description:
      "Automatically merge fulfillments that meet every selected requirement and none of the configured exceptions.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.autoMerge",
      product: "fulfillment",
    },
    risk: {
      level: "high",
      reason: "Changes which shipments are combined automatically.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=merging",
    },
  },
  {
    id: "fulfillment.remember_shipment_choices",
    title: "Remember shipment choices",
    description:
      "Reuse your package and weight choices for future shipments with matching contents.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.learnedPresets",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Reused package and weight choices can affect shipping rates and labels.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
    synonyms: ["automated learning"],
  },
  {
    id: "fulfillment.batch_suggestions",
    title: "Batch suggestions",
    description:
      "Group similar shipments for processing together. Each shipment stays separate.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.batchSuggestions",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason:
        "Changes which shipments are suggested for processing together, without merging them.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
    synonyms: ["bulk shipment suggestions", "bulk shipment matching"],
  },
  {
    id: "fulfillment.ship_from_locations",
    title: "Fulfillment locations",
    searchLabels: ["Return addresses"],
    description:
      "Manage Ship from locations and Return addresses used to originate and receive shipments.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes affect the origin selected for new manual orders.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=locations",
    },
    synonyms: [
      "ship from locations",
      "shipping origins",
      "warehouses",
      "fulfillment locations",
    ],
  },
  {
    id: "fulfillment.address",
    title: "Address validation",
    description:
      "Clean up shipping addresses, hold orders that need correction, and apply address holds and correction emails outside the US. Configure correction emails under Customer notifications.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.address",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes affect emails sent to customers and whether orders are held from fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
    synonyms: [
      "address verification",
      "address validation",
      "bad address",
      "address correction email",
      "address outreach",
      "international addresses",
    ],
  },
  {
    id: "fulfillment.scan_to_verify",
    title: "Scan to verify",
    description:
      "Controls how operators verify item quantities while packing shipments.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.scanVerify",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can allow operators to verify multiple items without scanning each unit.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "scanToVerify" },
    synonyms: ["scan to verify", "item verification", "packing verification"],
  },
  {
    id: "fulfillment.import_restrictions",
    title: "Import restrictions",
    description:
      "Import rules your catalog has tripped in screened cross-border markets, and whether you have marked each one resolved because you hold the required permit, certificate, or documentation. Resolving a rule stops it blocking publishing and pricing for every product that trips it.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "crdb",
      path: "restriction_measure_resolution",
      product: "fulfillment",
    },
    risk: {
      level: "high",
      reason:
        "Resolving a restriction publishes and prices products a destination country flagged as restricted; doing it without the real documentation risks customs seizure.",
    },
    agentEditability: {
      mode: "never",
      reason:
        "Only the merchant can attest that they hold the documentation a restriction requires.",
    },
    fullPageTarget: { type: "catalog", path: "restrictions" },
    synonyms: [
      "import restrictions",
      "restricted products",
      "customs restrictions",
      "certificate of conformity",
      "permit required",
      "prohibited items",
      "resolve restriction",
    ],
  },
  {
    id: "fulfillment.printing",
    title: "Documents",
    description:
      "How shipping labels, packing slips and other fulfillment documents are produced and ordered when a warehouse prints them.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes what a warehouse physically prints and packs against, so a wrong setting can put the wrong items in a box.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=printing",
    },
    synonyms: [
      "printing settings",
      "print documents",
      "warehouse printing",
      "label and slip printing",
    ],
  },
  {
    id: "fulfillment.outbound_notifications",
    title: "Outbound labels notifications",
    description:
      "Which outbound shipping events send an email to the team's configured notification addresses.",
    productArea: "fulfillment",
    access: directMerchantAccess(SettingsPermissions.SET_GENERAL_SETTINGS),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.internalNotifications",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason:
        "Controls internal notification emails only; no customer-facing or billing behavior changes.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "notifications?tab=outbound-labels",
    },
    synonyms: ["outbound labels notifications", "shipping notification emails"],
  },
  {
    id: "fulfillment.transfers",
    title: "Receiving and transfers",
    description:
      "Controls what happens to inventory between the dock and a pickable bin: put-away order, which bins to pull from, and lot tracking.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.wms",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason:
        "Only changes the order put-away items are presented in; bins and quantities are unaffected.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "oms/scanning-warehouse" },
    synonyms: ["inventory transfer settings", "transfer put-away"],
  },
  {
    id: "fulfillment.pick_sessions",
    title: "Picking",
    description:
      "Controls how pick sessions are built and what pickers can do in them in the warehouse mobile app.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.wms.pickSession",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes how orders are grouped into sessions and totes and how many scans a pick requires; wrong values slow pickers down but do not move inventory.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "oms/scanning-warehouse" },
    synonyms: [
      "pick session settings",
      "picking strategy",
      "allowed problem picks",
      "problem pick policy",
      "pick without inventory",
      "pick session filters",
      "pick session priority filters",
    ],
  },
  {
    id: "fulfillment.replenishment",
    title: "Replenishment",
    description:
      "Controls how many units the warehouse moves from backstock into pickable bins when a product is replenished.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.wms",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes how much inventory replenishment routes move between bins; wrong values over- or under-fill pickable bins but do not change on-hand totals.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "oms/scanning-warehouse" },
    synonyms: ["replenishment settings", "bin replenishment"],
  },
  {
    id: "fulfillment.dashboard",
    title: "Warehouse dashboard",
    description:
      "Controls which actions appear on the warehouse dashboard and which warehouse roles see each one.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.wms.dashboard",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason:
        "Only shows or hides dashboard shortcuts; every workflow stays reachable and no orders or inventory change.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "oms/scanning-warehouse?tab=dashboard",
    },
    synonyms: ["warehouse dashboard actions", "wms dashboard shortcuts"],
  },
  {
    id: "fulfillment.work_orders",
    title: "Work orders",
    description:
      "Controls work order stations and how work orders are prioritized.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.wms",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason:
        "Changes how work orders are prioritized and which stations process them; no inventory or orders change.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "oms/scanning-warehouse" },
    synonyms: [
      "work order settings",
      "work order stations",
      "work order priority filters",
    ],
  },
  {
    id: "fulfillment.general",
    title: "General warehouse",
    description:
      "Controls the default location warehouse devices open, the warehouse's totes, and how bins are named.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.wms",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason:
        "Changes which location warehouse devices default to; no inventory or orders change.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "oms/scanning-warehouse" },
    synonyms: [
      "general warehouse settings",
      "warehouse totes",
      "bin naming settings",
    ],
  },
  {
    id: "fulfillment.auto_split",
    title: "Auto-split",
    description:
      "How Redo separates matching items into their own fulfillment groups.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.autoSplit",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes affect how items are grouped into shipments during fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=splitting",
    },
    synonyms: ["automatic order splitting", "split fulfillment groups"],
  },
] as const satisfies readonly SettingGroupDefinition<FulfillmentFlatTeamSettingGroupId>[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const FulfillmentFlatTeamSettingGroupById = Object.fromEntries(
  FulfillmentFlatTeamSettingGroups.map((group) => [group.id, group]),
) as Record<
  FulfillmentFlatTeamSettingGroupId,
  SettingGroupDefinition<FulfillmentFlatTeamSettingGroupId>
>;
